import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../../hooks/useAppStore';
import { PageLayout } from '../shared/PageLayout';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../shared/SlideOver';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { MasteryBar } from '../shared/MasteryBar';
import { timeAgo } from '../shared/utils';
import { SearchIcon, UsersIcon, SchoolIcon, BookOpenIcon, Plus } from 'lucide-react';
import { Button } from '../../../ui/Button';
export function TeachersPage() {
  const {
    accounts,
    createAccount,
    getLearnerData
  } = useAppStore();
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  const [search, setSearch] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  // Add Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newTeacherName, setNewTeacherName] = useState('');
  const [newTeacherSchool, setNewTeacherSchool] = useState('');
  const [newTeacherEmail, setNewTeacherEmail] = useState('');
  const handleAddTeacher = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeacherName.trim()) return;
    createAccount('teacher', {
      displayName: newTeacherName,
      schoolName: newTeacherSchool,
      email: newTeacherEmail
    });
    setIsAddModalOpen(false);
    setNewTeacherName('');
    setNewTeacherSchool('');
    setNewTeacherEmail('');
  };
  const teachers = useMemo(() => allAccounts.filter(a => a.role === 'teacher'), [allAccounts]);
  const learners = useMemo(() => allAccounts.filter(a => a.role === 'learner'), [allAccounts]);
  const enrichedTeachers = useMemo(() => {
    return teachers.map(teacher => {
      const schoolLearners = learners.filter(l => l.schoolName === teacher.schoolName || accounts.relationships?.teacherToLearners?.[teacher.id]?.includes(l.id));
      const lastActive = teacher.lastActive ?? 0;
      return {
        ...teacher,
        studentCount: schoolLearners.length,
        lastActive: lastActive
      };
    });
  }, [teachers, learners, accounts.relationships]);
  const filtered = useMemo(() => {
    return enrichedTeachers.filter(t => {
      return !search || t.displayName.toLowerCase().includes(search.toLowerCase()) || (t.schoolName ?? '').toLowerCase().includes(search.toLowerCase());
    });
  }, [enrichedTeachers, search]);
  const selectedTeacher = useMemo(() => selectedTeacherId ? enrichedTeachers.find(t => t.id === selectedTeacherId) : null, [selectedTeacherId, enrichedTeachers]);
  const schoolLearners = useMemo(() => {
    if (!selectedTeacher) return [];
    return learners.filter(l => l.schoolName === selectedTeacher.schoolName || accounts.relationships?.teacherToLearners?.[selectedTeacher.id]?.includes(l.id)).map(learner => {
      const learnerData = getLearnerData(learner.id);
      const familyMastery = learnerData?.familyMastery || {};
      const masteryValues = Object.values(familyMastery).map(m => m.masteryScore);
      const avgMastery = masteryValues.length > 0 ? Math.round(masteryValues.reduce((sum, val) => sum + val, 0) / masteryValues.length) : 0;
      const totalAttempts = Object.values(familyMastery).reduce((sum, m) => sum + m.attempts, 0);
      const sessionCount = Math.ceil(totalAttempts / 10);
      return {
        ...learner,
        avgMastery,
        sessionCount
      };
    });
  }, [selectedTeacher, learners, accounts.relationships, getLearnerData]);
  const schools = useMemo(() => new Set(teachers.map(t => t.schoolName).filter(Boolean)).size, [teachers]);
  const columns = [{
    key: 'displayName',
    label: 'Naam',
    render: (v: string, row: (typeof filtered)[0]) => <div className="flex items-center gap-2.5" data-id="element-1118">
          <Avatar name={v} data-id="element-1119" />
          <div data-id="element-1120">
            <div className="text-sm font-medium text-gray-900" data-id="element-1121">{v}</div>
            <div className="text-xs text-gray-400" data-id="element-1122">{row.groupName ?? '—'}</div>
          </div>
        </div>
  }, {
    key: 'schoolName',
    label: 'School',
    render: (v: string) => <span className="text-sm text-gray-600" data-id="element-1123">{v ?? '—'}</span>
  }, {
    key: 'studentCount',
    label: 'Leerlingen',
    render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-1124">
          <UsersIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1125" />
          {v}
        </span>
  }, {
    key: 'lastActive',
    label: 'Laatste activiteit',
    render: (v: number) => <span className="text-xs text-gray-500" data-id="element-1126">{timeAgo(v)}</span>
  }];
  return <PageLayout title="Docenten" description={`${filtered.length} van ${teachers.length} docenten`} actions={<div className="flex gap-2" data-id="element-1128">
          <ConnectionBadge status="live" data-id="element-1129" />
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-1130">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-1131" />
            Docent toevoegen
          </Button>
        </div>} data-id="element-1127">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-1132">
        <StatCard label="Totaal docenten" value={teachers.length} icon={<UsersIcon className="w-5 h-5" data-id="element-1134" />} color="indigo" data-id="element-1133" />
        <StatCard label="Scholen" value={schools} icon={<SchoolIcon className="w-5 h-5" data-id="element-1136" />} color="purple" data-id="element-1135" />
        <StatCard label="Leerlingen totaal" value={learners.length} icon={<BookOpenIcon className="w-5 h-5" data-id="element-1138" />} color="blue" data-id="element-1137" />
      </div>

      <div className="relative mb-4" data-id="element-1139">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1140" />
        <input type="text" placeholder="Zoek op naam of school..." value={search} onChange={e => setSearch(e.target.value)} className="w-full max-w-sm pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1141" />
      </div>

      {filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center" data-id="element-1142">
          <UsersIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-1143" />
          <p className="text-sm text-gray-500" data-id="element-1144">Geen docenten gevonden</p>
        </div> : <DataTable columns={columns} data={filtered} onRowClick={row => setSelectedTeacherId(row.id)} data-id="element-1145" />}

      <SlideOver open={!!selectedTeacher} onClose={() => setSelectedTeacherId(null)} title={selectedTeacher?.displayName ?? ''} data-id="element-1146">
        {selectedTeacher && <div className="space-y-6" data-id="element-1147">
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-1148">
              <Avatar name={selectedTeacher.displayName} size="md" data-id="element-1149" />
              <div data-id="element-1150">
                <div className="font-semibold text-gray-900" data-id="element-1151">
                  {selectedTeacher.displayName}
                </div>
                <div className="text-sm text-gray-500" data-id="element-1152">
                  {selectedTeacher.schoolName ?? '—'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5" data-id="element-1153">
                  Laatste activiteit: {timeAgo(selectedTeacher.lastActive)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-1154">
              <div className="bg-indigo-50 rounded-lg p-3" data-id="element-1155">
                <div className="text-2xl font-bold text-indigo-700" data-id="element-1156">
                  {selectedTeacher.studentCount}
                </div>
                <div className="text-xs text-indigo-600" data-id="element-1157">
                  Leerlingen op school
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3" data-id="element-1158">
                <div className="text-2xl font-bold text-purple-700" data-id="element-1159">
                  {selectedTeacher.schoolName ? 1 : 0}
                </div>
                <div className="text-xs text-purple-600" data-id="element-1160">School</div>
              </div>
            </div>

            {schoolLearners.length > 0 && <div data-id="element-1161">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-1162">
                  Leerlingen op {selectedTeacher.schoolName}
                </h3>
                <div className="space-y-2" data-id="element-1163">
                  {schoolLearners.map(learner => <div key={learner.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-id="element-1164">
                      <div className="flex items-center gap-2" data-id="element-1165">
                        <Avatar name={learner.displayName} data-id="element-1166" />
                        <div data-id="element-1167">
                          <div className="text-sm text-gray-800" data-id="element-1168">
                            {learner.displayName}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-1169">
                            {learner.groupName ?? '—'}
                          </div>
                        </div>
                      </div>
                      <MasteryBar value={learner.avgMastery} data-id="element-1170" />
                    </div>)}
                </div>
              </div>}
          </div>}
      </SlideOver>

      {/* Add Teacher Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe docent" description="Voeg een docentaccount toe" data-id="element-1171">
        <form onSubmit={handleAddTeacher} className="space-y-4" data-id="element-1172">
          <div data-id="element-1173">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1174">
              Naam
            </label>
            <input type="text" required value={newTeacherName} onChange={e => setNewTeacherName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. Meester Jan" data-id="element-1175" />
          </div>

          <div data-id="element-1176">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1177">
              School (optioneel)
            </label>
            <input type="text" value={newTeacherSchool} onChange={e => setNewTeacherSchool(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Naam van de school" data-id="element-1178" />
          </div>

          <div data-id="element-1179">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1180">
              E-mailadres (optioneel)
            </label>
            <input type="email" value={newTeacherEmail} onChange={e => setNewTeacherEmail(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="docent@school.nl" data-id="element-1181" />
          </div>

          <div className="pt-4 flex justify-end gap-2" data-id="element-1182">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)} data-id="element-1183">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-1184">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>;
}